import React, { ReactElement, useMemo } from 'react';
import classNames from 'classnames';
import styles from './Header.scss';
import { ReactComponent as Close } from '../../assets/icons/header/close.svg';
import { ReactComponent as Arrow } from '../../assets/icons/header/arrow.svg';
import { getRgbA } from '../../services/colors';
import { useAppState } from '../../hooks/app-state';

export interface HeaderProps {
  title: string;
  subtitle?: string;
  onlineIndicator?: boolean;
  imageUrl?: string;
  imageSvg?: ReactElement;
  textColor: string;
  backgroundColor: string;
  font?: string;
  onClose?(): void;
  closeButtonLabel?: string;
  onBack?(): void;
  onButtonClick?(): void;
  buttonLabel?: React.ReactNode;
  buttonAriaLabel?: string;
}

export const Header: React.FunctionComponent<HeaderProps> = ({
  title,
  subtitle,
  onlineIndicator,
  imageUrl,
  imageSvg,
  textColor,
  backgroundColor,
  font,
  onClose,
  closeButtonLabel,
  onBack,
  onButtonClick,
  buttonLabel,
  buttonAriaLabel,
}) => {
  const { withQab: isEditorX } = useAppState().appState;
  const withImage = imageUrl || imageSvg;

  const decodedTitle = useMemo(() => {
    function decodeHTMLEntities(text: string) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }

    try {
      return decodeHTMLEntities(title);
    } catch (e) {
      return title;
    }
  }, [title]);

  const onlineIndicatorComponent = onlineIndicator ? (
    <div
      data-hook="online-indicator"
      className={styles.onlineIndicator}
      style={{
        borderColor: backgroundColor,
      }}
    />
  ) : undefined;

  const imageComponent = withImage ? (
    <div data-hook="image-wrapper" className={styles.imageWrapper}>
      {imageUrl && (
        <img
          className={styles.image}
          data-hook="image"
          src={imageUrl}
          style={{ borderColor: getRgbA(textColor, 0.05) }}
        />
      )}
      {imageSvg ? imageSvg : undefined}
      {onlineIndicatorComponent}
    </div>
  ) : undefined;

  const subtitleComponent = subtitle ? (
    <div data-hook="subtitle-wrapper" className={styles.subtitleWrapper}>
      {!withImage && onlineIndicatorComponent}
      <span data-hook="subtitle" className={styles.subtitle}>
        {subtitle}
      </span>
    </div>
  ) : undefined;

  return (
    <div
      data-hook="header"
      className={classNames(styles.header, {
        [styles.mobile]: isEditorX,
      })}
      style={{
        color: textColor,
        backgroundColor,
      }}
    >
      {onBack && (
        <button
          className={styles.backButton}
          data-hook="back-button"
          aria-label="chat-widget-header-back"
          onClick={onBack}
        >
          <Arrow width="14px" height="8px" />
        </button>
      )}
      {imageComponent}
      <div className={styles.content}>
        <h2
          data-hook="title"
          className={styles.title}
          style={{
            fontFamily: font,
          }}
        >
          {decodedTitle}
        </h2>
        {subtitleComponent}
      </div>
      {onButtonClick && (
        <button
          data-hook="custom-button"
          className={styles.customButton}
          onClick={onButtonClick}
          aria-label={buttonAriaLabel}
          style={{
            fontFamily: font,
          }}
        >
          {buttonLabel}
        </button>
      )}
      {onClose && (
        <button
          data-hook="close-button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={closeButtonLabel}
          autoFocus
          tabIndex={0}
        >
          <Close />
        </button>
      )}
    </div>
  );
};
